import { ColDef, SortModelItem, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import HyperlinkCellComponent from 'components/runs/HyperlinkCellComponent'
import {
  formatTimestampToHumanReadable,
  INSTRUMENT_REPORTED_TIMEFORMAT,
} from 'components/shared/date-utils'
import { startCase } from 'lodash'
import * as ROUTES from '../../constants/routes'
import CustomCellComponent from './CustomCellComponent'
import CustomCheckboxCellComponent from './CustomCheckboxCellComponent'
import ProgressCellComponent from './ProgressCellComponent'
import StatusCellComponent from './StatusCellComponent'
import TooltipHeaderComponent from './TooltipHeaderComponent'

/** Format a percentage with dynamic precision: 1dp if >= 1%, 2dp if 0.1-1%, scientific below 0.1% */
function formatPercentage(value: number): string {
  if (value < 0.1) return value.toExponential(1)
  if (value < 1) return value.toFixed(2)
  return value.toFixed(1)
}

// Tooltip text constants for column headers
export const TOOLTIP_STOP_COUNT = 'The target number of cells to sort before stopping'
export const TOOLTIP_TARGET_CELLS_SORTED =
  'The number of target cells sorted into this well. This count is used to determine if the stop count has been reached.'
export const TOOLTIP_TOTAL_CELLS_IN_WELL =
  'The total number of cells (including target and non-target cells) sorted into this well'
export const TOOLTIP_YIELD =
  'INTERNAL DEEPCELL ONLY: The percentage of target cells sorted into this well, while this well was active.'
export const TOOLTIP_PURITY =
  'INTERNAL DEEPCELL ONLY: The percentage of cells in this well that are target cells.  Calculated as: TP / (TP + FP) × 100.'
const PERCENT_IN_SAMPLE_BASE =
  'The percentage of this morphotype in the total cell sample. Calculated as: (morphotype count / total cell count) × 100.'

/** Denominator came from imaged_cell_count, which the instrument reports with beads excluded. */
export const TOOLTIP_PERCENT_IN_SAMPLE = `${PERCENT_IN_SAMPLE_BASE} The total cell count excludes beads.`

/**
 * Denominator fell back to total_cell_count. Runs from instruments that do not report a separate
 * bead count have no bead-excluded figure available, so the denominator still includes beads.
 */
export const TOOLTIP_PERCENT_IN_SAMPLE_WITH_BEADS = `${PERCENT_IN_SAMPLE_BASE} The total cell count includes beads, because this run's instrument did not report them separately.`

/**
 * Header tooltip for the Total Cell Count column on the runs grid. The grid shows many runs at
 * once and the bead-excluded figure is only available per run, so this wording has to hold for
 * both cases.
 */
export const TOOLTIP_TOTAL_CELL_COUNT =
  'The number of cells imaged during the run. Beads are excluded for runs whose instrument reported them separately; older runs include beads.'

const headerTemplate =
  '<div class="ag-cell-label-container" role="presentation">' +
  '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
  '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
  '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
  '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
  '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
  '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
  '    <span ref="eText" class="ag-header-cell-text" role="columnheader" style="white-space: normal;"></span>' +
  '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
  '  </div>' +
  '</div>'

/**
 * Aggregate the sum of a column
 * And it will return a number that will be equivalent to sum of all rows for a particular column.
 * @function aggSumFromString
 * @param field string.
 * @param api ag-grid API.
 */

export const enum RunsHeaderName {
  RunId = 'Run Id',
  RunType = 'Run Type',
  RunDescription = 'Run Description',
  Project = 'Project',
  StartTime = 'Start Time',
  Status = 'Status',
  Operator = 'Operator',
  TotalCellCount = 'Total Cell Count',
  ExperimentCode = 'Experiment Code',
  QualityReview = 'Quality Review',
  ChipBarcode = 'Chip Barcode',
}

export const columnDefs = (): ColDef[] => {
  return [
    {
      headerName: '',
      field: 'run_id',
      checkboxSelection: true,
      width: 50,
      pinned: 'left',
      cellRendererFramework: CustomCheckboxCellComponent,
    },
    {
      headerName: RunsHeaderName.RunDescription,
      field: 'description',
      minWidth: 300,
      flex: 1,
      pinned: 'left',
      cellRendererFramework: CustomCellComponent,
      cellRendererParams: {
        ellipsis: true,
      },
    },
    {
      headerName: RunsHeaderName.TotalCellCount,
      // Pinned so ag-Grid does not derive it from `field`. The displayed value falls back between
      // two columns, but only total_cell_count is sortable server side (SWT-1613).
      colId: 'total_cell_count',
      field: 'total_cell_count',
      width: 140,
      cellClass: 'ag-right-aligned-cell',
      valueGetter: (params) => params.data?.imaged_cell_count ?? params.data?.total_cell_count ?? 0,
      headerComponentFramework: TooltipHeaderComponent,
      headerComponentParams: {
        tooltipText: TOOLTIP_TOTAL_CELL_COUNT,
      },
      valueFormatter: (params) => {
        const value = (params.value as number) || 0
        if (value >= 1000000) {
          return `${(value / 1000000).toFixed(2)}M`
        }
        if (value >= 1000) {
          return `${(value / 1000).toFixed(2)}K`
        }
        return value.toString()
      },
    },
    {
      headerName: RunsHeaderName.RunType,
      field: 'well_sorting_configurations',
      width: 150,
      valueFormatter: (params) => (params.value?.length > 0 ? 'Sorting' : 'Imaging'),
      cellRendererFramework: CustomCellComponent,
    },
    {
      headerName: RunsHeaderName.StartTime,
      field: 'start_time',
      width: 200,
      valueFormatter: (params) =>
        formatTimestampToHumanReadable(params.value, INSTRUMENT_REPORTED_TIMEFORMAT) || '',
      cellRendererFramework: CustomCellComponent,
      cellRendererParams: {
        ellipsis: true,
      },
    },

    {
      headerName: RunsHeaderName.Operator,
      field: 'user_email',
      cellRendererFramework: CustomCellComponent,
      cellRendererParams: {
        ellipsis: true,
      },
      sortable: true,
    },
    {
      headerName: RunsHeaderName.Status,
      field: 'stopped',
      width: 160,
      sortable: true,
      valueFormatter: (params) =>
        (params.value as boolean)
          ? startCase(params.data.stop_reason) || 'Complete'
          : 'In Progress',
    },
    {
      headerName: RunsHeaderName.RunId,
      field: 'run_id',
      width: 200,
      cellStyle: {
        color: '#5F55D1',
      },
      lockPinned: true,
      cellRendererFramework: CustomCellComponent,
    },
    {
      headerName: RunsHeaderName.ChipBarcode,
      field: 'chip_barcode',
      hide: true, // Hidden from grid view but included in exports via allColumns: true
    },
    {
      headerName: RunsHeaderName.QualityReview,
      field: 'run_quality_score',
      width: 140,
      cellRendererFramework: StatusCellComponent,
      valueFormatter: (params) => params.data?.run_quality_score,
    },
  ]
}

export const detailColumnDefs: ColDef[] = [
  {
    headerName: 'Run Id',
    field: 'run_id',
    width: 190,
    sort: 'desc',
    sortable: true,
  },
  {
    headerName: 'Sample Id',
    field: 'run_id',
    width: 190,
    sort: 'desc',
    sortable: true,
  },
  {
    headerName: 'Project',
    field: 'project_code',
    width: 200,
    sortable: true,
  },
  {
    headerName: 'Experiment Code',
    field: 'experiment_code',
    width: 200,
    sortable: true,
  },
  {
    headerName: 'Run Description',
    field: 'description',
    width: 200,
    sortable: true,
  },
  {
    headerName: 'Run Notes',
    field: 'experiment_code',
    width: 200,
    sortable: true,
  },
  {
    headerName: 'Target Morphotypes',
    field: 'target_cell_classes',
    width: 200,
    sortable: true,
  },
]

export interface SortByWellColumnDefsOptions {
  isInternalUser?: boolean
  updatedMetricsEnabled?: boolean
  hasNewDataFormat?: boolean
}

export const getSortByWellColumnDefs = (options: SortByWellColumnDefsOptions = {}): ColDef[] => {
  const { isInternalUser, updatedMetricsEnabled, hasNewDataFormat } = options

  // Common column definitions
  const wellColumn: ColDef = {
    colId: 'well',
    headerName: 'Well',
    field: 'well',
    minWidth: 45,
    flex: 0.5,
    resizable: false,
  }

  const morphotypesColumn: ColDef = {
    colId: 'cell_types',
    headerName: 'Morphotypes',
    field: 'cell_types',
    flex: 2,
    minWidth: 100,
    headerClass: 'wrap-header',
  }

  const thresholdColumn: ColDef = {
    colId: 'threshold',
    headerName: 'Threshold',
    field: 'threshold',
    hide: !isInternalUser,
    minWidth: 40,
    flex: 0.5,
    resizable: false,
  }

  const stopCountColumn: ColDef = {
    colId: 'stop_count',
    headerName: 'Stop Count',
    field: 'stop_count',
    minWidth: 65,
    flex: 1,
    resizable: false,
    headerClass: 'wrap-header',
    ...(updatedMetricsEnabled
      ? {
        headerComponentFramework: TooltipHeaderComponent,
        headerComponentParams: {
          tooltipText: TOOLTIP_STOP_COUNT,
        },
      }
      : {
        headerComponentParams: {
          template: headerTemplate,
        },
      }),
  }

  const yieldColumn: ColDef = {
    colId: 'yield',
    headerName: 'Yield',
    field: 'true_positive_rate',
    minWidth: 35,
    flex: 0.5,
    valueFormatter: (params) => {
      const value = params.value ?? 0
      return `${value.toFixed(1)}%`
    },
    hide: !isInternalUser,
    resizable: false,
    headerComponentFramework: TooltipHeaderComponent,
    headerComponentParams: {
      tooltipText: TOOLTIP_YIELD,
    },
  }

  const purityColumn: ColDef = {
    colId: 'purity',
    headerName: 'Purity',
    field: 'purity',
    minWidth: 35,
    flex: 0.5,
    valueFormatter: ({ value }) => {
      const val = value ?? 0
      return `${val.toFixed(1)}%`
    },
    hide: !isInternalUser,
    resizable: false,
    headerComponentFramework: TooltipHeaderComponent,
    headerComponentParams: {
      tooltipText: TOOLTIP_PURITY,
    },
  }

  // Build column array based on feature flag
  const columns: ColDef[] = [wellColumn, morphotypesColumn, thresholdColumn, stopCountColumn]

  if (updatedMetricsEnabled) {
    // New experience: show new columns if data format supports it
    if (hasNewDataFormat) {
      columns.push({
        colId: 'true_positive_count',
        headerName: 'Target Cells Sorted',
        field: 'true_positive_count',
        flex: 1.5,
        minWidth: 130,
        headerClass: 'wrap-header',
        cellRendererFramework: ProgressCellComponent,
        valueFormatter: (params) => params.data?.true_positive_count ?? 0,
        valueGetter: (params) => {
          const count = params.data?.true_positive_count ?? 0
          const stopCount = params.data?.stop_count ?? 1
          return (count / stopCount) * 100
        },
        headerComponentFramework: TooltipHeaderComponent,
        headerComponentParams: {
          tooltipText: TOOLTIP_TARGET_CELLS_SORTED,
        },
      })

      columns.push({
        colId: 'total_cells_in_well_count',
        headerName: 'Total Cells in Well',
        field: 'total_cells_in_well_count',
        flex: 1.5,
        minWidth: 100,
        resizable: false,
        hide: true,
        headerClass: 'wrap-header',
        valueFormatter: ({ value }) => value ?? 0,
        headerComponentFramework: TooltipHeaderComponent,
        headerComponentParams: {
          tooltipText: TOOLTIP_TOTAL_CELLS_IN_WELL,
        },
      })
    }
  } else {
    // Old experience: show sorted cells count
    columns.push({
      colId: 'sorted_cells_count',
      headerName: 'Sorted Cells Count',
      field: 'count',
      minWidth: 130,
      flex: 1.5,
      resizable: true,
      headerClass: 'wrap-header',
      cellRendererFramework: ProgressCellComponent,
      valueFormatter: (params) => params.data?.count ?? 0,
      valueGetter: (params) => {
        return (params.data.count / params.data.stop_count) * 100
      },
      headerComponentParams: {
        template: headerTemplate,
      },
    })
  }

  // Add yield and purity columns (common to both experiences)
  columns.push(yieldColumn, purityColumn)

  return columns
}

export const getQCMetricsColumnDefs = (runId: string, quadrants: string[]): ColDef[] => [
  {
    headerName: 'Run %',
    field: 'run_percent',
    pinned: 'left',
    width: 80,
    cellStyle: { 'font-weight': 'bold' },
  },
  {
    headerName: 'Browse Cells',
    pinned: 'left',
    width: 100,
    headerComponentParams: {
      template: headerTemplate,
    },
    valueGetter: (params: ValueGetterParams) => {
      const page = quadrants.findIndex((quadrant) => quadrant === params.data.run_percent)
      return `${ROUTES.CELL_BROWSING}?activeTab=search&centerCrop=0&page=${page}&runId=${runId}&sharpen=0&showSortFields=1&sortOrderKey=SAMPLE_DISTRIBUTION`
    },
    cellRendererFramework: HyperlinkCellComponent,
  },
  {
    headerName: 'Partial/ Cut off Images',
    field: 'cutoff_image',
    width: 120,
    headerComponentParams: {
      template: headerTemplate,
    },
    editable: true,
  },
  {
    headerName: 'Contamination Covering Images',
    field: 'contamination_covering_image',
    width: 150,
    headerComponentParams: {
      template: headerTemplate,
    },
    editable: true,
  },
  {
    headerName: 'Chip Covering Blemish Images',
    field: 'chip_blemish_covering_image',
    width: 150,
    headerComponentParams: {
      template: headerTemplate,
    },
    editable: true,
  },
  {
    headerName: 'Blebbing Images',
    field: 'blebbing_image',
    width: 100,
    headerComponentParams: {
      template: headerTemplate,
    },
    editable: true,
  },
  {
    headerName: 'Cell Debris Images',
    field: 'cell_debris',
    minWidth: 120,
    flex: 1,
    headerComponentParams: {
      template: headerTemplate,
    },
    editable: true,
  },
]

export interface CellTypeColumnDefsOptions {
  runId: string
  total_cell_count: number
  model: string
  updatedMetricsEnabled?: boolean
  /** True when total_cell_count came from imaged_cell_count, which already excludes beads. */
  excludesBeads?: boolean
}

export const getCellTypeColumnDefs = (options: CellTypeColumnDefsOptions): ColDef[] => {
  const { runId, total_cell_count: totalCellCount, model, updatedMetricsEnabled, excludesBeads } = options

  const columns: ColDef[] = [
    {
      headerName: 'Morphotypes',
      field: 'cell_type',
      minWidth: 180,
      flex: 2,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value?.includes('Class')) {
          return params.value.replace('Class', '').toUpperCase()
        }
        return params.value
      },
    },
  ]

  // Only show "Sorted cells" column when feature is not enabled
  if (!updatedMetricsEnabled) {
    columns.push({
      headerName: 'Sorted cells',
      field: 'count',
      minWidth: 120,
      resizable: true,
      cellRendererFramework: ProgressCellComponent,
      valueFormatter: (params: ValueFormatterParams) => params.data?.count ?? 0,
      valueGetter: (params: ValueGetterParams) => {
        const count = params.data?.count ?? 0
        return (count / totalCellCount) * 100
      },
    })
  }

  // % in Sample column - add tooltip and progress bar when feature is enabled
  const percentInSampleColumn: ColDef = updatedMetricsEnabled
    ? {
      colId: 'percent_in_sample',
      headerName: '% in Sample',
      field: 'count',
      width: 140,
      cellStyle: { textAlign: 'left' },
      cellRendererFramework: ProgressCellComponent,
      valueFormatter: (params) => {
        const percentage = params.value ?? 0
        const formatted = formatPercentage(percentage)
        return `${formatted}%`
      },
      valueGetter: (params: ValueGetterParams) => {
        const count = params.data?.count ?? 0
        return (count / totalCellCount) * 100
      },
      headerComponentFramework: TooltipHeaderComponent,
      headerComponentParams: {
        tooltipText: excludesBeads
          ? TOOLTIP_PERCENT_IN_SAMPLE
          : TOOLTIP_PERCENT_IN_SAMPLE_WITH_BEADS,
      },
    }
    : {
      colId: 'percent_in_sample',
      headerName: '% in Sample',
      field: 'count',
      width: 140,
      cellStyle: { textAlign: 'left' },
      valueFormatter: (params: ValueFormatterParams) => {
        const count = params.value ?? 0
        const percentage = (count / totalCellCount) * 100
        const formatted = formatPercentage(percentage)
        return `${formatted}%`
      },
    }

  columns.push(
    percentInSampleColumn,
    {
      headerName: 'Browse Details',
      field: 'cell_type',
      minWidth: 120,
      flex: 1,
      valueGetter: (params: ValueGetterParams) => {
        const cellType = params.data?.cell_type

        if (!cellType) return ''

        // TODO: Refactor this logic when backend provides explicit classifier type information.
        // Current implementation relies on model name pattern matching which is fragile.
        // Backend should provide a 'classifierType' field that explicitly indicates whether
        // this is a built-in classifier (predictedClasses) or a morphology classifier.

        // Check if the model used is a XGBoost - CR3.1 and CR4.2 classifiers
        if (model.toUpperCase().includes('XGBOOST')) {
          // Handle as predicted class
          return `${ROUTES.CELL_BROWSING}?sortOrderKey=TIME_ASC&runId=${runId}&predictedClasses=${encodeURIComponent(cellType)}`
        }

        // Handle as morphology classifier
        const modelParts = model.split(' v')
        const modelVersion = modelParts[modelParts.length - 1]
        const modelName = modelParts.slice(0, modelParts.length - 1).join(' v')
        const classifier = `${cellType}:v${modelVersion}:${modelName}`
        const encodedClassifier = encodeURIComponent(classifier)

        return `${ROUTES.CELL_BROWSING}?sortOrderKey=TIME_ASC&runId=${runId}&morphologyClassifiers=${encodedClassifier}`
      },
      cellRendererFramework: HyperlinkCellComponent,
      cellStyle: { textAlign: 'left' },
    }
  )

  return columns
}

/**
 * Transform SortModelItem to a API "order_by" string param.
 * For example [ { colId: 'project_code', sort: 'asc' }, { colId: 'start_time', sort: 'desc' } ] will result in:
 * "order_by=project_code:asc,start_time:desc".
 * And it will sort ascending by project code first, then descending by start time.
 * @function sortModelItemToOrderBy
 * @param sorts Array<SortModelItem> | The SortModelItem to transform.
 */
export function sortModelItemToOrderBy(sorts: Array<SortModelItem>): string | null {
  try {
    // try to reduce each sortModelItem to a plain string that will be use in
    // the API call as a "order_by" param, in the form : "order_by=sorts[0].colId:sorts[0].sort,sorts[1].colId:sorts[1].sort"
    return sorts.map((s) => `${s.colId}:${s.sort}`).join(',')
  } catch {
    return null
  }
}
